import React from "react"
import {Integration} from "../types"
import * as TablerIcons from "@tabler/icons-react"
import {IconCheck, IconClock, IconPlus, IconSettings} from "@tabler/icons-react"

interface IntegrationCardProps {
  integration: Integration
  onToggle: (id: string) => void
  onManage?: (integration: Integration) => void
}

export function IntegrationCard({
  integration,
  onToggle,
  onManage,
}: IntegrationCardProps) {
  // Get the icon component dynamically
  const IconComponent =
    (TablerIcons as any)[integration.icon] || TablerIcons.IconPlug

  const handleManageClick = () => {
    if (onManage) {
      onManage(integration)
    }
  }

  return (
    <div className="relative overflow-hidden rounded-lg border border-black/5 bg-white shadow-sm transition-all duration-200 hover:shadow-md">
      {/* Header with icon and status */}
      <div className="flex items-start justify-between gap-3 p-4">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <div
            className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg"
            style={{backgroundColor: `${integration.color}15`}}
          >
            <IconComponent size={24} style={{color: integration.color}} />
          </div>
          <div className="relative min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h3 className="flex-shrink-0 font-semibold text-gray-900">
                {integration.name}
              </h3>
              {integration.isNew && (
                <span className="flex-shrink-0 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
                  New
                </span>
              )}
            </div>
            <p
              className={`text-sm text-gray-500 ${integration.comingSoon || integration.enabled ? "relative" : ""}`}
            >
              {integration.description}
              {/* Blur overlay only for description when coming soon or enabled */}
              {(integration.comingSoon || integration.enabled) && (
                <div className="absolute inset-0 rounded bg-gray-50/60 backdrop-blur-[1px]"></div>
              )}
            </p>
          </div>
        </div>

        {/* Status indicator */}
        <div className="flex flex-shrink-0 items-center gap-2">
          {integration.enabled ? (
            // TODO: Uncomment when we support managing integrations
            // <button
            //   onClick={handleManageClick}
            //   className="flex items-center gap-1 whitespace-nowrap rounded-md bg-green-100 px-3 py-1.5 text-xs font-medium text-green-700 transition-colors hover:bg-green-200"
            // >
            //   <IconSettings size={14} />
            //   Manage Integration
            // </button>
            // TODO: Delete this div when we support managing integrations
            <div className="flex cursor-default select-none items-center gap-1 whitespace-nowrap rounded-md bg-green-100 px-3 py-1.5 text-xs font-medium text-green-700">
              <IconCheck size={14} />
              Connected
            </div>
          ) : integration.comingSoon ? (
            <div className="flex items-center gap-1 text-orange-600">
              <IconClock size={16} />
              <span className="whitespace-nowrap text-xs font-medium">
                Coming Soon
              </span>
            </div>
          ) : (
            <button
              onClick={() => onToggle(integration.id)}
              className="flex items-center gap-1 whitespace-nowrap rounded-md bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-200"
            >
              <IconPlus size={14} />
              Connect
            </button>
          )}
        </div>
      </div>

      {/* Integration types */}
      <div className="relative border-t border-gray-50 px-4 py-3">
        <div className="flex flex-wrap gap-1">
          {integration.types.map((type) => (
            <span
              key={type}
              className="rounded-full bg-gray-100 px-2 py-1 text-xs text-gray-600"
            >
              {type}
            </span>
          ))}
        </div>
        {/* Blur overlay for types section when coming soon or enabled */}
        {(integration.comingSoon || integration.enabled) && (
          <div className="absolute inset-0 bg-gray-50/60 backdrop-blur-[1px]"></div>
        )}
      </div>

      {/* Connected banner across bottom */}
      {integration.enabled && (
        <div className="border-t border-green-200 bg-green-50 px-4 py-2">
          <div className="flex items-center gap-2">
            <IconCheck size={16} className="text-green-600" />
            <span className="text-sm font-semibold text-green-700">
              Connected
            </span>
          </div>
        </div>
      )}
    </div>
  )
}
